import { useRef } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import defaultStyles from "./CarList.module.scss";
import { useCarSlot } from "~core-cars/slots";
import type { VehicleAvailabilityWithSupplier } from "~core-cars/store";
import { useCarsStyles } from "~core-cars/themes";
import { CoreCarsComponent } from "~core-cars/types";

export type CarListProps = {
  list: VehicleAvailabilityWithSupplier[];
} & PlatformComponentProps;

const COMPONENT_KEY = CoreCarsComponent.CarList;
export const CarList = ({ list, className }: CarListProps) => {
  const styles = useCarsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const CarSlot = useCarSlot();

  const listRef = useRef(null);

  const virtualizer = useWindowVirtualizer({
    count: list.length,
    estimateSize: () => 45,
    overscan: 5,
  });

  return (
    <div ref={listRef} className={clsx(block, className)}>
      <ul
        className={cn("list")}
        style={{
          position: "relative",
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const car = list[virtualItem.index];

          return (
            <li
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              className={cn("list-item")}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <CarSlot.CarAvailabilityCard
                car={car}
                index={virtualItem.index}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};
